import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Folder from "@mui/icons-material/Folder";
import FolderCopy from "@mui/icons-material/FolderCopy";
import MoreVert from "@mui/icons-material/MoreVert";
import { openPath } from "@tauri-apps/plugin-opener";
import { useSelector } from "@xstate/react";
import { useRouter } from "next/router";
import { type FC, useCallback } from "react";

import { DropdownMenu } from "@moment/design-system/DropdownMenu";

import { useEasel } from "~/components/canvas/easel";
import { copyContent } from "~/components/common/CopyText";
import { useDeleteConfirmModal } from "~/components/common/useDeleteConfirmModal";
import { getDocumentDirPath } from "~/data/filesystem";
import { useDocumentActor } from "~/state";
import { CANVAS_NAME, HOMEPAGE_ROUTE } from "~/strings";
import { IconButton } from "~/ui/Button";
import { toTitleCase } from "~/utils/format/string";

export type HeaderPopOverProps = {
	onRenameCanvas: () => void;
};

export const HeaderPopOver: FC<HeaderPopOverProps> = ({ onRenameCanvas }) => {
	const router = useRouter();

	const { canvasRef, documentId } = useEasel();
	const { deleteConfirmation } = useDeleteConfirmModal();

	const documentActor = useDocumentActor(documentId);
	const document = useSelector(documentActor, (state) => state);

	const copyDocumentPath = useCallback(async () => {
		const path = await getDocumentDirPath(documentId);
		if (!path) {
			return;
		}
		void copyContent(path, "Copied document path to clipboard");
	}, [documentId]);

	const handleDeleteDocument = async () => {
		if (canvasRef.current === null) {
			return;
		}

		const confirmStatus = await deleteConfirmation({
			name: canvasRef.current.title || `this ${CANVAS_NAME.SINGULAR}`,
			action: "delete",
			confirmLabel: "Delete",
		});

		if (!confirmStatus || canvasRef.current === null) {
			return;
		}

		documentActor?.send({
			type: "document.deleted",
		});

		void router.push(HOMEPAGE_ROUTE);
	};

	// const handleArchiveDocument = async () => {
	// 	if (canvasRef.current === null) return;

	// 	const confirmStatus = await deleteConfirmation({
	// 		name: canvasRef.current.title || `this ${CANVAS_NAME.SINGULAR}`,
	// 		action: "archive",
	// 		confirmLabel: "Archive",
	// 	});

	// 	if (!confirmStatus || canvasRef.current === null) {
	// 		return;
	// 	}

	// 	void dispatch(
	// 		documentArchived({
	// 			workspaceName: "local",
	// 			documentId: canvasRef.current.id,
	// 			pageId: canvasRef.current.id,
	// 		})
	// 	);
	// };

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<IconButton
					data-testid="canvas-header-pop-over-trigger"
					className="relative ml-2 !h-6 !w-6 inline-block !rounded-full"
					preset="quaternary"
					size="xs"
					label={`Show ${CANVAS_NAME.SINGULAR} overflow menu`}
					icon={<MoreVert />}
				/>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="mb-2" align="start">
					<DropdownMenu.Item onSelect={() => onRenameCanvas()}>
						<Edit className={"mr-2"} />
						Rename document
					</DropdownMenu.Item>

					<DropdownMenu.Separator />

					<DropdownMenu.Item
						onSelect={async () => {
							console.log("openDocumentDirInOs", documentId);
							const path = await getDocumentDirPath(documentId);
							if (!path) {
								return;
							}
							await openPath(path);
						}}
					>
						<Folder className={"mr-2"} />
						Reveal document on filesystem…
					</DropdownMenu.Item>

					<DropdownMenu.Item onSelect={copyDocumentPath}>
						<FolderCopy className={"mr-2"} />
						Copy path to document folder
					</DropdownMenu.Item>

					<DropdownMenu.Separator />

					<DropdownMenu.Item
						data-testid="delete-document-item"
						variant="negative"
						onSelect={() => handleDeleteDocument()}
						disabled={document?.matches("deleting")}
					>
						<Delete className={"mr-2"} />
						Delete {toTitleCase(CANVAS_NAME.SINGULAR)}
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
};
